/**
 * Hero3DScene — True 3D WebGL hero (React Three Fiber)
 *
 * IMPORTANT: This file deliberately uses React.createElement instead of JSX.
 * The dev-mode visual-edits babel plugin injects `x-line-number` / `x-file-name`
 * attributes into every JSX element, which crashes R3F when applied to Three.js
 * objects ("R3F: Cannot set x-line-number"). createElement calls are not
 * instrumented, keeping the 3D scene graph clean. Do NOT convert back to JSX.
 */
import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useTexture, Float, Stars, Sparkles } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';

const h = React.createElement;

/* ---------- Camera rig: drifts + tilts with mouse in real 3D ---------- */
const CameraRig = () => {
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    state.camera.position.x +=
      (state.pointer.x * 1.1 + Math.sin(t * 0.15) * 0.25 - state.camera.position.x) * 0.04;
    state.camera.position.y +=
      (state.pointer.y * 0.7 + Math.cos(t * 0.2) * 0.15 + 0.6 - state.camera.position.y) * 0.04;
    state.camera.lookAt(0, 1.1, 0);
  });
  return null;
};

/* ---------- Radial glow texture (generated once) ---------- */
const makeGlowTexture = (r, g, b) => {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');
  const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0, `rgba(${r},${g},${b},0.55)`);
  grad.addColorStop(0.4, `rgba(${r},${g},${b},0.18)`);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
};

const CLOUDS = [
  { tex: 'violet', pos: [-3.5, 2.6, -4], scale: 9 },
  { tex: 'cyan', pos: [3.8, -0.5, -5], scale: 8 },
  { tex: 'violet', pos: [0.5, 0.8, -6], scale: 12 },
  { tex: 'cyan', pos: [-2.5, -1.8, -4.5], scale: 6 },
  { tex: 'violet', pos: [4.2, 3.2, -6.5], scale: 7 },
];

/* ---------- Volumetric nebula fog (drifting additive sprites) ---------- */
const Nebula = () => {
  const group = useRef();
  const violet = useMemo(() => makeGlowTexture(139, 92, 246), []);
  const cyan = useMemo(() => makeGlowTexture(0, 212, 255), []);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    group.current.children.forEach((s, i) => {
      s.position.x = CLOUDS[i].pos[0] + Math.sin(t * 0.08 + i * 2.1) * 0.8;
      s.position.y = CLOUDS[i].pos[1] + Math.cos(t * 0.06 + i * 1.7) * 0.5;
      s.material.rotation = t * 0.02 * (i % 2 ? 1 : -1);
    });
  });
  return h(
    'group',
    { ref: group },
    CLOUDS.map((c, i) =>
      h(
        'sprite',
        { key: i, position: c.pos, scale: [c.scale, c.scale, 1] },
        h('spriteMaterial', {
          map: c.tex === 'violet' ? violet : cyan,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          transparent: true,
          opacity: 0.5,
        })
      )
    )
  );
};

/* ---------- The logo floating in 3D with aura glow ---------- */
const Logo = () => {
  // const tex = useTexture('/logo-hero.png');
  const aura = useMemo(() => makeGlowTexture(168, 85, 247), []);
  return h(
    Float,
    { speed: 1.6, rotationIntensity: 0.12, floatIntensity: 0.55 },
    h(
      'mesh',
      null,
      h('planeGeometry', { args: [4.3, 2.81] }),
      h('meshBasicMaterial', { color: '#8b5cf6', transparent: true, opacity: 0.4, toneMapped: false, depthWrite: false })
    ),
    h(
      'sprite',
      { position: [0, 0, -0.4], scale: [6.5, 6.5, 1] },
      h('spriteMaterial', {
        map: aura,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
        opacity: 0.75,
      })
    )
  );
};

/* ---------- Gyroscope portal ring + traveling glow orb ---------- */
const Ring = ({ radius, color, speed, axis, tilt }) => {
  const ref = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed;
    ref.current.rotation.x = tilt[0] + (axis === 'x' ? t : 0);
    ref.current.rotation.y = tilt[1] + (axis === 'y' ? t : 0);
    ref.current.rotation.z = tilt[2] + (axis === 'z' ? t : 0);
  });
  return h(
    'group',
    { ref },
    h(
      'mesh',
      null,
      h('torusGeometry', { args: [radius, 0.012, 8, 160] }),
      h('meshBasicMaterial', { color, transparent: true, opacity: 0.55, toneMapped: false })
    ),
    h(
      'mesh',
      { position: [radius, 0, 0] },
      h('sphereGeometry', { args: [0.05, 16, 16] }),
      h('meshBasicMaterial', { color, toneMapped: false })
    )
  );
};

const Portal = () =>
  h(
    'group',
    null,
    h(Ring, { radius: 2.55, color: '#00d4ff', speed: 0.28, axis: 'y', tilt: [Math.PI / 2.4, 0, 0] }),
    h(Ring, { radius: 2.75, color: '#a855f7', speed: -0.2, axis: 'x', tilt: [0, Math.PI / 3, Math.PI / 8] }),
    h(Ring, { radius: 2.95, color: '#c084fc', speed: 0.14, axis: 'z', tilt: [Math.PI / 2.1, Math.PI / 5, 0] })
  );

/* ---------- Sacred geometry: Flower of Life wireframe ---------- */
const FlowerOfLife = () => {
  const group = useRef();
  const centers = useMemo(() => {
    const r = 1.15;
    const pts = [[0, 0]];
    for (let i = 0; i < 6; i++) {
      pts.push([r * Math.cos((i * Math.PI) / 3), r * Math.sin((i * Math.PI) / 3)]);
    }
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3;
      pts.push([2 * r * Math.cos(a), 2 * r * Math.sin(a)]);
      pts.push([
        r * Math.cos(a) + r * Math.cos(a + Math.PI / 3),
        r * Math.sin(a) + r * Math.sin(a + Math.PI / 3),
      ]);
    }
    return pts;
  }, []);
  const circleGeo = useMemo(() => {
    const pts = new THREE.EllipseCurve(0, 0, 1.15, 1.15).getPoints(72);
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, []);
  useFrame((state) => {
    group.current.rotation.z = state.clock.elapsedTime * 0.04;
  });
  return h(
    'group',
    { ref: group, position: [0, 0, -2.2], scale: 1.35 },
    centers.map(([x, y], i) =>
      h(
        'lineLoop',
        { key: i, geometry: circleGeo, position: [x, y, 0] },
        h('lineBasicMaterial', { color: '#8b5cf6', transparent: true, opacity: 0.14 })
      )
    )
  );
};

/* ---------- Responsive scaling for the whole scene ---------- */
const SceneContent = () => {
  const { viewport } = useThree();
  const scale = Math.min(1, Math.max(0.58, viewport.width / 8.5));
  return h(
    'group',
    { position: [0, 1.15, 0], scale },
    h(FlowerOfLife),
    h(Portal)
    // h(Sparkles, { count: 90, scale: [9, 6, 5], size: 2.2, speed: 0.35, opacity: 0.55, color: '#c4b5fd' })
  );
};

/* ---------- Main canvas ---------- */
const Hero3DScene = () =>
  h(
    Canvas,
    {
      dpr: [1, 1.5],
      camera: { position: [0, 0.6, 9], fov: 42 },
      gl: { antialias: false, alpha: true, powerPreference: 'high-performance' },
      style: { position: 'absolute', inset: 0 },
    },
    h(CameraRig),
    h(Stars, { radius: 60, depth: 40, count: 1800, factor: 4, saturation: 0.4, fade: true, speed: 0.6 }),
    h(Nebula),
    h(SceneContent)
    // h(Effects)
  );

/* ---------- Adaptive postprocessing: skip bloom on software renderers ---------- */
function Effects() {
  const { gl } = useThree();
  const isSoftware = useMemo(() => {
    try {
      const ctx = gl.getContext();
      const ext = ctx.getExtension('WEBGL_debug_renderer_info');
      const renderer = ext ? ctx.getParameter(ext.UNMASKED_RENDERER_WEBGL) : '';
      return /swiftshader|llvmpipe|software/i.test(String(renderer));
    } catch (e) {
      return true;
    }
  }, [gl]);
  if (isSoftware) return null;
  return h(
    EffectComposer,
    { multisampling: 0 },
    h(Bloom, {
      intensity: 1.15,
      luminanceThreshold: 0.18,
      luminanceSmoothing: 0.9,
      mipmapBlur: true,
      radius: 0.75,
    }),
    h(Vignette, { eskil: false, offset: 0.18, darkness: 0.75 })
  );
}

export default Hero3DScene;
